import React from 'react';
import {
  Wallet,
  PiggyBank,
  ChartLineUp,
  Coins,
  Clock,
  ArrowsClockwise,
} from '@phosphor-icons/react';

const GlobalInfo = ({ data }) => {
  if (!data) return null;

  const totalBudget = data.total_budget || 0;
  const currentBalance = data.current_balance || 0;
  const currentInvestment = data.current_investment || 0;
  const totalProfit = data.total_profit || 0;
  const finishedProfit = data.finished_profit || 0;
  const activeProfit = data.active_profit || 0;

  const balancePercent =
    totalBudget > 0 ? ((currentBalance / totalBudget) * 100).toFixed(1) : 0;
  const investmentPercent =
    totalBudget > 0 ? ((currentInvestment / totalBudget) * 100).toFixed(1) : 0;

  const isProfitPositive = totalProfit > 0;
  const isProfitNegative = totalProfit < 0;

  return (
    <section className="global-summary-container">
      <div className="summary-header-strip">
        <span className="summary-section-title">
          <ChartLineUp size={16} weight="bold" className="title-icon" />
          Portfolio Overview
        </span>
        <div className="summary-meta-badges">
          {data.created_at && (
            <div className="meta-badge" title="Strategy Started Date">
              <Clock size={13} weight="bold" />
              <span>
                Started: {new Date(data.created_at).toLocaleDateString()}
              </span>
            </div>
          )}
          {data.updated_at && (
            <div className="meta-badge" title="Last Sync with Brokerage">
              <ArrowsClockwise size={13} weight="bold" />
              <span>
                Synced:{' '}
                {new Date(data.updated_at).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="global-info-grid">
        {/* Total Budget */}
        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-label">Total Budget</span>
            <div className="metric-icon-wrap neutral">
              <Wallet size={18} weight="duotone" />
            </div>
          </div>
          <div className="metric-value-wrap">
            <span className="metric-value mono">
              {totalBudget.toLocaleString()}
            </span>
            <span className="metric-unit">KRW</span>
          </div>
          <div className="metric-subtext">Total allocated capital</div>
        </div>

        {/* Current Balance */}
        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-label">Available Balance</span>
            <div className="metric-icon-wrap info">
              <PiggyBank size={18} weight="duotone" />
            </div>
          </div>
          <div className="metric-value-wrap">
            <span className="metric-value mono">
              {currentBalance.toLocaleString()}
            </span>
            <span className="metric-unit">KRW</span>
          </div>
          <div className="metric-progress-bar">
            <div
              className="progress-fill info"
              style={{
                width: `${Math.min(100, Math.max(0, balancePercent))}%`,
              }}
            />
          </div>
          <div className="metric-subtext mono">
            {balancePercent}% in reserve
          </div>
        </div>

        {/* Current Investment */}
        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-label">Active Investment</span>
            <div className="metric-icon-wrap primary">
              <Coins size={18} weight="duotone" />
            </div>
          </div>
          <div className="metric-value-wrap">
            <span className="metric-value mono">
              {currentInvestment.toLocaleString()}
            </span>
            <span className="metric-unit">KRW</span>
          </div>
          <div className="metric-progress-bar">
            <div
              className="progress-fill primary"
              style={{
                width: `${Math.min(100, Math.max(0, investmentPercent))}%`,
              }}
            />
          </div>
          <div className="metric-subtext mono">
            {investmentPercent}% currently deployed
          </div>
        </div>

        {/* Total Aggregate Profit */}
        <div
          className={`metric-card highlight ${
            isProfitPositive ? 'positive' : isProfitNegative ? 'negative' : ''
          }`}
        >
          <div className="metric-header">
            <span className="metric-label">Total Realized Profit</span>
            <div
              className={`metric-icon-wrap ${
                isProfitPositive
                  ? 'success'
                  : isProfitNegative
                    ? 'danger'
                    : 'neutral'
              }`}
            >
              <ChartLineUp size={18} weight="bold" />
            </div>
          </div>
          <div className="metric-value-wrap">
            <span
              className={`metric-value mono ${
                isProfitPositive
                  ? 'text-success'
                  : isProfitNegative
                    ? 'text-danger'
                    : ''
              }`}
            >
              {totalProfit > 0 ? '+' : ''}
              {totalProfit.toLocaleString()}
            </span>
            <span className="metric-unit">KRW</span>
          </div>
          {(finishedProfit !== 0 || activeProfit !== 0) && (
            <div className="metric-subtext mono breakdown">
              <span>Closed: {finishedProfit.toLocaleString()}</span>
              <span className="divider">•</span>
              <span>Open: {activeProfit.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default GlobalInfo;
